import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ApiResponse } from '../../menu/types';
import { invalidateGridDataCache } from './invalidateGridDataCache';

export const WISE_API = '/umbraco/api/Wise';

/** Outcome of one invoice, as the legacy Wise controller returns it. */
export type WiseInvoiceSendResult = {
  InvoiceId: number;
  InvoiceNumber?: string | null;
  Success: boolean;
  TransferId?: number | null;
  Amount?: number | null;
  Currency?: string | null;
  Error?: string | null;
};

/** Outcome of one send run; Error is set only when nothing could be attempted. */
export type WiseSendResult = {
  Error?: string | null;
  SentCount: number;
  FailedCount: number;
  Results: WiseInvoiceSendResult[];
};

/** Outcome of removing the Wise mark; skipped invoices had no mark, paid ones keep it. */
export type WiseUnmarkResult = {
  Error?: string | null;
  ClearedCount: number;
  SkippedCount: number;
  PaidCount?: number;
};

/** Every grid showing the invoices must refetch once their Wise mark changed. */
const invalidateGridsAfterSuccess = async (
  queryFulfilled: Promise<unknown>,
  dispatch: Parameters<typeof invalidateGridDataCache>[0]
) => {
  try {
    await queryFulfilled;
    invalidateGridDataCache(dispatch);
  } catch {
    // The handler reports the error; nothing to invalidate on failure.
  }
};

export const wiseApi = createApi({
  reducerPath: 'wiseApi',
  baseQuery,
  endpoints: (builder) => ({
    unmarkInvoicesInWise: builder.mutation<WiseUnmarkResult, number[]>({
      query: (invoiceIds) => ({
        url: `${WISE_API}/UnmarkInvoices`,
        method: 'POST',
        body: { invoiceIds },
      }),
      transformResponse: (response: ApiResponse<WiseUnmarkResult>) => {
        if ((response.Code ?? response.code) === 1 && response.data) {
          return response.data;
        }
        throw new Error(extractErrorMessage(response, 'Failed to remove the Wise mark.'));
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
    sendInvoicesToWise: builder.mutation<WiseSendResult, number[]>({
      query: (invoiceIds) => ({
        url: `${WISE_API}/SendInvoices`,
        method: 'POST',
        body: { invoiceIds },
      }),
      transformResponse: (response: ApiResponse<WiseSendResult>) => {
        if ((response.Code ?? response.code) === 1 && response.data) {
          return response.data;
        }
        throw new Error(extractErrorMessage(response, 'Failed to send invoices to Wise.'));
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
  }),
});
